import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Chip, FormControlLabel, Grid, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Add, ContentCopyOutlined, DragIndicator, LockOutlined, RestartAltRounded } from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import ClearIcon from 'assets/doc_icons/close_icon.svg';
import CustomInputField from 'ui-components/CustomInputField';
import { Secondary } from 'ui-components/Buttons';
import { planVersionInfo } from 'utils/index';
import PlanCheckbox from './PlanCheckbox';
import SegmentedControl from './SegmentedControl';
import Section from './Section';
import Field from './Field';
import PriceMatrix from './PriceMatrix';
import ConfirmDialog from './ConfirmDialog';
import BillingModal from './BillingModal';
import PlanCardPreview from './PlanCardPreview';


// enabled and visible are two flags whose combinations an admin has to decode,
// so they are edited as the one thing they describe: whether a plan is on sale.
const STATUS_OPTIONS = [
    { value: 'live', label: 'Live', hint: 'Shown on the pricing page and available to buy' },
    { value: 'hidden', label: 'Hidden', hint: 'Not offered to new customers; existing subscriptions keep billing' },
    { value: 'off', label: 'Off', hint: 'Switched off everywhere' },
];

const statusOf = (plan) => (plan?.enabled === false ? 'off' : plan?.visible === false ? 'hidden' : 'live');
const applyStatus = (plan, status) => ({
    ...plan,
    enabled: status !== 'off',
    visible: status === 'live',
});

/**
 * What each limit is called in the interface, and what it does.
 *
 * The config's own key names — max_count, storage_limit_mb, upgrade_allowed —
 * describe the schema, not the thing being limited, and reading them off the
 * screen tells you nothing about what a founder will hit. The hints say what
 * happens when someone reaches the number, because that is the part that is
 * not guessable from the label.
 */
const LIMIT_GROUPS = {
    invitations: {
        title: 'Investor outreach',
        fields: {
            enabled: { label: 'Allow automated outreach' },
            daily_limit: { label: 'Invites per day' },
            monthly_limit: { label: 'Invites per month' },
        },
    },
    data_rooms: {
        title: 'Data rooms',
        fields: {
            enabled: { label: 'Allow data rooms' },
            upgrade_allowed: { label: 'Prompt to upgrade at the limit' },
            max_count: { label: 'Data rooms' },
            storage_limit_mb: { label: 'Total storage (MB)' },
        },
    },
    funding_rounds: {
        title: 'Funding rounds',
        fields: {
            upgrade_allowed: { label: 'Prompt to upgrade at the limit' },
            // Every round counts, whatever its status: only one round can be
            // Open at a time for anyone, so a limit of 1, 3 or 5 is only
            // meaningful as a total across draft, open and closed.
            max_count: { label: 'Funding rounds, including closed ones' },
            // The one on/off that is shown. The rest stay hidden, as below.
            apply_to_existing: {
                label: 'Apply to existing startups',
                shown: true,
                hint: 'On, every startup on this plan gets the number above straight away. Off, the ones already subscribed keep what they had when they bought.',
            },
        },
    },
    team_members: {
        title: 'Team',
        fields: {
            upgrade_allowed: { label: 'Prompt to upgrade at the limit' },
            // The owner does not spend a seat, so this is how many people the
            // founder can bring in besides themselves.
            max_count: { label: 'Team members, not counting the owner' },
        },
    },
    pitch_deck_reports: {
        title: 'Pitch Deck reports',
        fields: {
            monthly_limit: { label: 'Pitch Deck analyses per month' },
            upgrade_allowed: { label: 'Prompt to upgrade at the limit' },
        },
    },
    fundability_reports: {
        title: 'Fundability reports',
        fields: {
            monthly_limit: { label: 'Fundability reports per month' },
            upgrade_allowed: { label: 'Prompt to upgrade at the limit' },
        },
    },
};

const limitLabel = (group, name) =>
    LIMIT_GROUPS[group]?.fields?.[name]?.label
    // A limit added to the config but not named here still renders, just in the
    // config's own words, rather than disappearing from the screen.
    || name.replaceAll('_', ' ');

// Used by the group headings, which are commented out in the limits section.
// eslint-disable-next-line no-unused-vars
const limitGroupTitle = (group) =>
    LIMIT_GROUPS[group]?.title || group.replaceAll('_', ' ');

const GROUPS = [
    {
        heading: 'Live',
        note: 'What visitors see and can buy today.',
        match: (status) => status === 'live',
    },
    {
        heading: 'Hidden',
        note: 'Taken down, but still billing the people already on them.',
        match: (status) => status !== 'live',
    },
];

const PlanRail = ({ plans, publishedPlans, selected, onSelect }) => {
    const theme = useTheme();

    return (
        <Box sx={{
            backgroundColor: theme.palette.customColors.whiteBG,
            border: `1px solid ${theme.palette.customColors.b50}`,
            borderRadius: '8px',
            padding: '0.5rem',
            display: 'grid',
            gap: '0.25rem',
            alignContent: 'start',
            // Stays put while the editor beside it scrolls, so it is always
            // clear which plan is being edited.
            position: 'sticky',
            top: 0,
        }}>
            {/* Grouped by what a customer would see, with the heading said out
                loud. The list is otherwise five similar names, and which of them
                are actually on sale is the first thing a reader needs — and the
                one thing the names alone cannot tell them. */}
            {GROUPS.map(({ heading, note, match }) => {
                const entries = Object.entries(plans).filter(([, plan]) => match(statusOf(plan)));
                if (!entries.length) return null;

                return (
                    <Box key={heading} sx={{ marginBottom: '0.25rem' }}>
                        <Typography sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                            color: theme.palette.customColors.g500,
                            padding: '0.5rem 0.75rem 0.125rem',
                        }}>
                            {heading}
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.75rem',
                            color: theme.palette.customColors.g200,
                            padding: '0 0.75rem 0.375rem',
                            lineHeight: 1.4,
                        }}>
                            {note}
                        </Typography>

            {entries.map(([planKey, plan]) => {
                const { version, showVersion } = planVersionInfo({ plans }, planKey);
                const status = statusOf(plan);
                const isSelected = planKey === selected;

                return (
                    <Box
                        key={planKey}
                        component="button"
                        type="button"
                        aria-current={isSelected ? 'true' : undefined}
                        onClick={() => onSelect(planKey)}
                        sx={{
                            textAlign: 'left',
                            font: 'inherit',
                            border: 'none',
                            width: '100%',
                            padding: '0.625rem 0.75rem',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? theme.palette.customColors.indigo50 : 'transparent',
                            '&:hover': { backgroundColor: isSelected ? theme.palette.customColors.indigo50 : '#f7f7f8' },
                            '&:focus-visible': { outline: `2px solid ${theme.palette.customColors.indigo700}`, outlineOffset: '2px' },
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                            <Typography sx={{
                                fontSize: '0.9375rem',
                                fontWeight: 700,
                                lineHeight: 1.4,
                                color: isSelected ? theme.palette.customColors.indigo700 : theme.palette.customColors.g300,
                            }}>
                                {plan.title || planKey}
                            </Typography>
                            {showVersion && (
                                <Tooltip title={version > 1
                                    ? 'The current version of this plan. New subscribers buy this one.'
                                    : 'An earlier version, kept so its existing subscribers keep billing.'}>
                                    <Chip size="small" label={`V${version}`} sx={{ borderRadius: '4px', height: 20 }} />
                                </Tooltip>
                            )}
                        </Box>
                        {/* Only the status, when there is one to report. The plan
                            key is an implementation detail an admin does not need. */}
                        {status === 'off' && (
                            <Typography sx={{ fontSize: '0.75rem', color: theme.palette.customColors.g100 }}>
                                Off
                            </Typography>
                        )}
                        {/* Which plan you have edited, in the list you use to move
                            between them. The save bar counts the changes but not
                            where they are, so editing two plans in a row otherwise
                            leaves nothing on screen saying so. */}
                        {publishedPlans && JSON.stringify(publishedPlans[planKey]) !== JSON.stringify(plan) && (
                            <Typography sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: theme.palette.customColors.indigo700,
                            }}>
                                Edited, not published
                            </Typography>
                        )}
                    </Box>
                );
            })}
                    </Box>
                );
            })}
        </Box>
    );
};

/**
 * Copies the wording and limits from another plan. Setting up a repriced
 * version of a plan means retyping its whole feature list otherwise, which is
 * how the two generations drift apart.
 */
// Rendered by the Content section's action, which is commented out for now.
// eslint-disable-next-line no-unused-vars
const CopyFromMenu = ({ planKey, plans, onCopy }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const others = Object.entries(plans || {}).filter(([key]) => key !== planKey);
    if (!others.length) return null;

    const label = (key, plan) => {
        const { version, showVersion } = planVersionInfo({ plans }, key);
        const title = plan?.title || key;
        return showVersion ? `${title} V${version}` : title;
    };

    return (
        <>
            <Tooltip title="Copy the wording and limits from another plan">
                <span>
                    <Secondary startIcon={<ContentCopyOutlined />} onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ padding: '0.375rem 1rem' }}>
                        Copy from another plan
                    </Secondary>
                </span>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {others.map(([key, other]) => (
                    <MenuItem key={key} onClick={() => { onCopy(other); setAnchorEl(null); }}>
                        {label(key, other)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

const PlanEditor = ({ planKey, plan, plans, onChange, problemsFor, published, onReset }) => {
    const theme = useTheme();
    const [billingOpen, setBillingOpen] = React.useState(false);
    const [statusConfirm, setStatusConfirm] = React.useState(null);

    /**
     * Taking a plan off the pricing page is the one change here that customers
     * notice immediately and that nothing else on the screen warns about, so it
     * is confirmed. Putting one back on, or editing a plan already off the page,
     * is applied without asking.
     */
    const requestStatusChange = (status) => {
        const current = statusOf(plan);
        if (current === 'live' && status !== 'live') {
            setStatusConfirm(status);
            return;
        }
        onChange(applyStatus(plan, status));
    };
    const update = (changes) => onChange({ ...plan, ...changes });

    const updateFeature = (index, changes) => {
        const features = [...(plan.features || [])];
        features[index] = { ...features[index], ...changes };
        update({ features });
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const features = [...(plan.features || [])];
        const [moved] = features.splice(result.source.index, 1);
        features.splice(result.destination.index, 0, moved);
        update({ features });
    };

    const problems = problemsFor(`plans.${planKey}`);

    return (
        <Box display="grid" gap="1rem">
            {problems.length > 0 && (
                <Box sx={{
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    backgroundColor: theme.palette.customColors.r50,
                    border: `1px solid ${theme.palette.customColors.r75}`,
                }}>
                    {problems.map((problem) => (
                        <Typography key={problem.path} sx={{ fontSize: '0.8125rem', color: theme.palette.customColors.r500 }}>
                            {problem.message}
                        </Typography>
                    ))}
                </Box>
            )}

            <Section
                title="Content"
                description="The name, line beneath it and button customers see on this plan's card."
                action={
                    <Box display="flex" gap={1}>
                        {/* Copy one plan's content onto another. Hidden for now;
                            uncomment to bring it back.
                        <CopyFromMenu planKey={planKey} plans={plans} onCopy={(source) => update({
                            title: source.title,
                            subtitle: source.subtitle,
                            buttonText: source.buttonText,
                            features: JSON.parse(JSON.stringify(source.features || [])),
                            limits: JSON.parse(JSON.stringify(source.limits || {})),
                        })} />
                        */}
                        {published && JSON.stringify(published) !== JSON.stringify(plan) && (
                            <Tooltip title="Undo every unpublished change to this plan">
                                <span>
                                    <Secondary startIcon={<RestartAltRounded />} onClick={onReset} sx={{ padding: '0.375rem 1rem' }}>
                                        Reset
                                    </Secondary>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                }
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <CustomInputField label="Title" value={plan.title || ''}
                            onChange={(event) => update({ title: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustomInputField label="Subtitle" value={plan.subtitle || ''}
                            onChange={(event) => update({ subtitle: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CustomInputField label="Button text" value={plan.buttonText || ''}
                            onChange={(event) => update({ buttonText: event.target.value })} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* Each option carries its own hint, so the field does not
                            repeat them above the control. */}
                        <Field label="Status">
                            <SegmentedControl
                                ariaLabel="Plan status"
                                value={statusOf(plan)}
                                options={STATUS_OPTIONS}
                                onChange={(status) => requestStatusChange(status)}
                            />
                        </Field>
                        <FormControlLabel
                            sx={{ marginTop: '0.5rem', marginLeft: 0 }}
                            control={
                                <PlanCheckbox checked={Boolean(plan.popular)}
                                    onChange={(event) => update({ popular: event.target.checked })}
                                />
                            }
                            label={<Typography sx={{ fontSize: '0.875rem' }}>Show the "Most popular" badge</Typography>}
                        />
                    </Grid>
                </Grid>
            </Section>

            <Section
                title="Features"
                description="Listed on the plan card in this order. Drag to reorder."
            >
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId={`features-${planKey}`} direction="vertical">
                        {(dropProvided) => (
                            <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps} display="grid" gap="0.5rem">
                                {(plan.features || []).map((feature, index) => (
                                    <Draggable key={`${planKey}-feature-${index}`} draggableId={`${planKey}-feature-${index}`} index={index}>
                                        {(dragProvided, snapshot) => (
                                            <Box
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 1,
                                                    backgroundColor: snapshot.isDragging
                                                        ? theme.palette.customColors.indigo50
                                                        : theme.palette.customColors.whiteBG,
                                                    border: `1px solid ${theme.palette.customColors.b50}`,
                                                    borderRadius: '8px',
                                                    padding: '0.375rem 0.75rem',
                                                    ...dragProvided.draggableProps.style,
                                                }}
                                            >
                                                <Box {...dragProvided.dragHandleProps} sx={{ display: 'flex', cursor: 'grab', color: theme.palette.customColors.g75 }}>
                                                    <DragIndicator fontSize="small" />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: '0.5rem' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <CustomInputField
                                                                value={feature.text || ''}
                                                                inputProps={{ 'aria-label': `Feature bullet ${index + 1}` }}
                                                                placeholder="What the plan includes"
                                                                onChange={(event) => updateFeature(index, { text: event.target.value })}
                                                                sx={{
                                                                    marginBottom: 0,
                                                                    '& .MuiFilledInput-root': { border: 'none', background: 'transparent' },
                                                                    '& .MuiFilledInput-input': { padding: '8px 4px' },
                                                                }}
                                                            />
                                                        </Box>
                                                        <FormControlLabel
                                                            sx={{ marginRight: 0, flexShrink: 0 }}
                                                            control={<PlanCheckbox checked={feature.hide_icon !== true}
                                                                onChange={(event) => updateFeature(index, { hide_icon: !event.target.checked })} />}
                                                            label={<Typography sx={{ fontSize: '0.8125rem' }}>Tick</Typography>}
                                                        />
                                                        <FormControlLabel
                                                            sx={{ marginRight: 0, flexShrink: 0 }}
                                                            control={<PlanCheckbox checked={Boolean(feature.highlight)}
                                                                onChange={(event) => updateFeature(index, { highlight: event.target.checked })} />}
                                                            label={<Typography sx={{ fontSize: '0.8125rem' }}>Highlight</Typography>}
                                                        />
                                                        <FormControlLabel
                                                            sx={{ marginRight: 0, flexShrink: 0 }}
                                                            control={<PlanCheckbox checked={Boolean(feature.disabled)}
                                                                onChange={(event) => updateFeature(index, { disabled: event.target.checked })} />}
                                                            label={<Typography sx={{ fontSize: '0.8125rem' }}>Not included</Typography>}
                                                        />
                                                        <FormControlLabel
                                                            sx={{ marginRight: 0, flexShrink: 0 }}
                                                            control={<PlanCheckbox checked={feature.info_icon === true}
                                                                onChange={(event) => updateFeature(index, { info_icon: event.target.checked })} />}
                                                            label={<Typography sx={{ fontSize: '0.8125rem' }}>Info icon</Typography>}
                                                        />
                                                        <Tooltip title="Remove bullet">
                                                            <img
                                                                src={ClearIcon}
                                                                alt="Remove bullet"
                                                                style={{ height: '1rem', width: '1rem', cursor: 'pointer', flexShrink: 0 }}
                                                                onClick={() => update({ features: plan.features.filter((_, i) => i !== index) })}
                                                            />
                                                        </Tooltip>
                                                    </Box>
                                                    {/* Only offered while the info icon is ticked, since the icon is what shows it. */}
                                                    {feature.info_icon === true && (
                                                        <CustomInputField
                                                            label="Info (shown on hover or tap)"
                                                            multiline
                                                            minRows={3}
                                                            value={feature.info || ''}
                                                            inputProps={{ 'aria-label': `Feature info ${index + 1}` }}
                                                            placeholder="Write what the visitor should read after tapping the icon"
                                                            onChange={(event) => updateFeature(index, { info: event.target.value })}
                                                            sx={{ marginBottom: 0 }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </Draggable>
                                ))}
                                {dropProvided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </DragDropContext>

                <Secondary startIcon={<Add />} sx={{ marginTop: '0.75rem' }}
                    onClick={() => update({ features: [...(plan.features || []), { text: '' }] })}>
                    Add bullet
                </Secondary>
            </Section>

            <Section
                title="Usage limits"
                description="Applied when someone subscribes. Existing subscribers keep the limits from the day they bought."
            >
                <Grid container spacing={2}>
                    {Object.entries(plan.limits || {}).map(([group, limits]) => {
                        // A switch and a count are different kinds of answer, so
                        // the on/off sits under the heading and the numbers line
                        // up in a row of their own rather than sharing one grid.
                        const entries = Object.entries(limits || {});
                        // const flags = entries.filter(([, value]) => typeof value === 'boolean');
                        const counts = entries.filter(([, value]) => typeof value !== 'boolean');
                        // The on/off flags are hidden by default (see below). A
                        // field opts back in with `shown` rather than the whole
                        // set coming back.
                        const shownFlags = entries.filter(
                            ([name, value]) => typeof value === 'boolean' && LIMIT_GROUPS[group]?.fields?.[name]?.shown,
                        );

                        return (
                            <Grid item xs={12} md={6} key={group}>
                                {/* Group headings — "Investor outreach", "Data rooms" —
                                    hidden for now. The fields below name themselves.
                                    Uncomment to bring them back.
                                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700 }}>
                                    {limitGroupTitle(group)}
                                </Typography>
                                */}

                                {/* The on/off flags — "Allow automated outreach", "Allow
                                    data rooms", "Prompt to upgrade at the limit" — are
                                    hidden for now. The values stay in the config and keep
                                    being enforced; only the controls are out of the
                                    screen. Uncomment to bring them back.
                                {flags.map(([name, value]) => (
                                    <FormControlLabel key={name}
                                        sx={{ marginLeft: 0, marginBottom: '0.25rem' }}
                                        control={<PlanCheckbox checked={value}
                                            onChange={(event) => update({
                                                limits: { ...plan.limits, [group]: { ...limits, [name]: event.target.checked } },
                                            })} />}
                                        label={<Typography sx={{ fontSize: '0.8125rem' }}>{limitLabel(group, name)}</Typography>}
                                    />
                                ))}
                                */}

                                <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(8rem, 1fr))" gap={1.5}>
                                    {counts.map(([name, value]) => (
                                        <CustomInputField
                                            key={name}
                                            label={limitLabel(group, name)}
                                            type="number"
                                            value={value ?? ''}
                                            onChange={(event) => update({
                                                limits: {
                                                    ...plan.limits,
                                                    [group]: { ...limits, [name]: event.target.value === '' ? 0 : Number(event.target.value) },
                                                },
                                            })}
                                            sx={{ marginBottom: 0 }}
                                        />
                                    ))}
                                </Box>

                                {/* After the numbers, since a flag like "apply to
                                    existing" is about the number it follows. The
                                    explanation is a tooltip: it is long, and it
                                    pushes the next field down when left inline. */}
                                {shownFlags.map(([name, value]) => (
                                    <Tooltip key={name} title={LIMIT_GROUPS[group]?.fields?.[name]?.hint || ''}>
                                        <FormControlLabel
                                            sx={{ marginLeft: 0, marginTop: '0.5rem' }}
                                            control={<PlanCheckbox checked={!!value}
                                                onChange={(event) => update({
                                                    limits: { ...plan.limits, [group]: { ...limits, [name]: event.target.checked } },
                                                })} />}
                                            label={<Typography sx={{ fontSize: '0.8125rem' }}>{limitLabel(group, name)}</Typography>}
                                        />
                                    </Tooltip>
                                ))}
                            </Grid>
                        );
                    })}
                </Grid>
            </Section>

            <Section
                title={<>Billing</>}
                description="Prices on the page, and the Razorpay plan each purchase is charged against."
                action={
                    <Secondary startIcon={<LockOutlined />} onClick={() => setBillingOpen(true)} sx={{ padding: '0.375rem 1rem' }}>
                        Edit billing
                    </Secondary>
                }
            >
                <PriceMatrix plan={plan} />
            </Section>

            <ConfirmDialog
                open={Boolean(statusConfirm)}
                title={`Take ${plan.title || planKey} off the pricing page?`}
                body={statusConfirm === 'off'
                    ? 'It disappears from the pricing page and stops being sold. People already subscribed keep their plan until it is cancelled.'
                    : 'New visitors will not see it or be able to buy it. People already subscribed keep billing as normal.'}
                confirmLabel="Take it off"
                onCancel={() => setStatusConfirm(null)}
                onConfirm={() => {
                    onChange(applyStatus(plan, statusConfirm));
                    setStatusConfirm(null);
                }}
            />

            <BillingModal
                open={billingOpen}
                handleClose={() => setBillingOpen(false)}
                planKey={planKey}
                plan={plan}
                onApply={(updated) => onChange(updated)}
            />
        </Box>
    );
};

const PlanCardsEditor = ({ plans, publishedPlans, billing, onChange, problemsFor, onPlanSelected }) => {
    const planKeys = Object.keys(plans || {});
    const [selected, setSelected] = React.useState(planKeys[0]);

    React.useEffect(() => {
        if (!planKeys.includes(selected)) setSelected(planKeys[0]);
    }, [planKeys, selected]);

    if (!selected) return null;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={2.5}>
                <PlanRail
                    plans={plans}
                    publishedPlans={publishedPlans}
                    selected={selected}
                    onSelect={(planKey) => {
                        setSelected(planKey);
                        onPlanSelected?.();
                    }}
                />
            </Grid>
            <Grid item xs={12} md={6.5}>
                <PlanEditor
                    key={selected}
                    planKey={selected}
                    plan={plans[selected]}
                    plans={plans}
                    published={publishedPlans?.[selected]}
                    problemsFor={problemsFor}
                    onReset={() => onChange({ ...plans, [selected]: JSON.parse(JSON.stringify(publishedPlans[selected])) })}
                    onChange={(updated) => onChange({ ...plans, [selected]: updated })}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                <PlanCardPreview plan={plans[selected]} billing={billing} />
            </Grid>
        </Grid>
    );
};

export default PlanCardsEditor;
